/**
 * <CheckpointReplies> — les réponses que Sam propose, dans un checkpoint (molecule).
 *
 * Sam pose une question et propose ses réponses ; l'élève en touche une, et c'est un ENVOI, pas
 * une sélection qui reste. Deux formes, selon ce qui est proposé (la règle de l'ancien code,
 * `isCompactChoice`) :
 *  - des phrases (2 à 5), chacune dans sa carte, à la manière des cartes de démarrage de Sam
 *    (`<SamWelcome>`) : le texte, et en bout la pastille de ce que la réponse ouvre — prendre une
 *    photo, revenir à l'activité, écrire, choisir la date du contrôle ;
 *  - beaucoup de réponses courtes et sans geste (les treize classes, du CP à « Autre ») : des
 *    pilules (`<ChoiceChip variant="pill">`) qui passent à la ligne, la même écriture que le choix
 *    d'une classe partout ailleurs.
 *
 * `chosenKey` : la réponse touchée, pendant qu'elle part ou que son geste est ouvert (la saisie,
 * la date) ; elle se marque, les autres ne répondent plus.
 *
 * Composes DS pieces only — `<Card>`, `<Text>`, `<IconBubble>`, `<ChoiceChip>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ChoiceChip } from "../../atoms/ChoiceChip"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { type IconName } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { frenchSpacing } from "../../utils/frenchSpacing"
import { Card } from "../Card"
import { type UICheckpointReply, type UICheckpointReplyAction } from "../../../ports/primary/ui/types/studentCheckpoint"

// Une réponse proposée par Sam : sa forme vit dans `ports/primary/ui/types` (socle du live).
export type { UICheckpointReply, UICheckpointReplyAction }

export type CheckpointRepliesProps = {
    replies: UICheckpointReply[]
    onSelect: (key: string) => void
    /** La réponse touchée : elle part, ou son geste est ouvert. Les autres ne répondent plus. */
    chosenKey?: string | null
    /** Toute la rangée attend — Sam répond. Défaut `false`. */
    disabled?: boolean
    testID?: string
}

const ACTION_ICONS: Record<UICheckpointReplyAction, IconName> = {
    photo: "camera",
    activity: "tasks",
    text: "edit",
    date: "calendar-alt",
}

/** À partir de combien de réponses courtes la liste passe en pilules. */
const COMPACT_MIN_REPLIES = 6
/** Au-delà de cette longueur, une réponse se lit mieux en ligne qu'en pilule. */
const COMPACT_MAX_LABEL = 16

/** Beaucoup de réponses courtes, sans geste : des pilules. Les bords sont ceux de l'ancien code. */
function isCompactReplies(replies: UICheckpointReply[]): boolean {
    return (
        replies.length >= COMPACT_MIN_REPLIES &&
        replies.every((reply) => reply.action == null && reply.label.length <= COMPACT_MAX_LABEL)
    )
}

export function CheckpointReplies(props: CheckpointRepliesProps) {
    const { replies, onSelect, chosenKey, disabled = false, testID } = props

    if (replies.length === 0) return null

    const locked = disabled || chosenKey != null

    if (isCompactReplies(replies)) {
        return (
            <View style={styles.pills} testID={testID}>
                {replies.map((reply) => (
                    <ChoiceChip
                        key={reply.key}
                        label={reply.label}
                        variant="pill"
                        selected={reply.key === chosenKey}
                        disabled={locked && reply.key !== chosenKey}
                        onPress={() => onSelect(reply.key)}
                    />
                ))}
            </View>
        )
    }

    return (
        <View style={styles.list} testID={testID}>
            {replies.map((reply) => {
                const chosen = reply.key === chosenKey
                return (
                    <Card
                        key={reply.key}
                        pressable
                        onPress={() => onSelect(reply.key)}
                        disabled={locked && !chosen}
                        p="3"
                        style={styles.reply(chosen)}
                    >
                        <View style={styles.row}>
                            <Text size="text-md" weight="semibold" color="text-primary" style={styles.label}>
                                {frenchSpacing(reply.label)}
                            </Text>
                            {reply.action != null && (
                                <IconBubble
                                    iconName={ACTION_ICONS[reply.action]}
                                    size={36}
                                    iconSize={18}
                                    color="role-student-tint"
                                    iconColor="role-student-tint-ink"
                                />
                            )}
                        </View>
                    </Card>
                )
            })}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    pills: {
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: theme.spacing["2"],
    },
    list: {
        gap: theme.spacing["2"],
    },
    /**
     * La réponse touchée se lit au contour, comme une option choisie. 2 px dans les deux états, à la
     * couleur de la carte quand rien n'est touché : toucher ne décale rien d'un pixel.
     */
    reply: (chosen: boolean) => ({
        borderWidth: 2,
        borderColor: chosen ? theme.colors.role : theme.colors["surface-raised"],
    }),
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["3"],
        minHeight: 36,
    },
    label: {
        flex: 1,
    },
}))
